import React from 'react';
import { Modal, ModalProps } from '@mantine/core';
import './ThemedModal.css';
import { useIsMobile } from '../../../src/context/MediaQueryContext';
import { DropdownPortalContext } from '../../../src/context/DropdownPortalContext';
import { OverlayLayerContext, OVERLAY_LAYER_STEP, useParentOverlayZIndex } from '../../../src/context/OverlayLayerContext';

export interface ThemedModalProps extends ModalProps {
  /**
   * Defaults to 200 on a page, or 1000 above the drawer/modal it renders
   * inside (#2384). Pass a value only to pin the layer.
   */
  zIndex?: number;
  marginTop?: string;
  navbarOffset?: boolean;
  /** On mobile, expand to full screen with scrollable content. Use for payment forms. */
  fullScreenOnMobile?: boolean;
}

export const ThemedModal: React.FC<ThemedModalProps> = ({
  title,
  zIndex: zIndexProp,
  size,
  styles,
  marginTop,
  navbarOffset = true,
  fullScreenOnMobile = false,
  classNames,
  ...props
}) => {
  const isMobile = useIsMobile();
  const parentZIndex = useParentOverlayZIndex();

  // Inside a drawer (2000) the old fixed 200 put the modal behind it (#2384).
  const zIndex = zIndexProp ?? (parentZIndex !== null ? parentZIndex + OVERLAY_LAYER_STEP : 200);

  const isFullScreen = fullScreenOnMobile && isMobile;

  // A full-screen modal has to sit ABOVE the page's fixed chrome, or the
  // navbar (z 1000) paints over its header and close button, the help FAB
  // (z 1000) lands on whatever button is bottom-right, and the mobile cart
  // bar (z 200) stays visible underneath showing a stale total. Measured on
  // checkout at 375px: the X was unreachable and the FAB covered 16% of
  // Confirm & Pay. See #1146.
  //
  // Additive rather than a fixed value so a modal opened FROM a full-screen
  // modal keeps layering above it — AgreementDocument passes 300, so
  // checkout lands at 1200 and the policy reader at 1300. ThemedDrawer
  // (2000) and dropdowns (10000) stay above both. Desktop keeps the caller's
  // value: there the modal is centred over a dimmed overlay and the navbar
  // staying bright above it is the intended look.
  const effectiveZIndex = isFullScreen ? zIndex + 1000 : zIndex;

  // Full screen on mobile overrides size/offset; otherwise use responsive width
  const modalSize = isFullScreen ? '100%' : isMobile ? '90vw' : size || 'md';

  const navbarOffsetValue = !isFullScreen && navbarOffset
    ? isMobile
      ? 'calc(64px + 4px)'
      : 'calc(80px + 1rem)'
    : undefined;

  // Tall content scrolls inside the modal instead of running off the bottom of
  // the viewport. This used to be `visible` so inline dropdowns could escape the
  // modal, which left a pass editor's Save button unreachable at 1024x768
  // (#1875). Dropdowns now portal out instead, via DropdownPortalContext below.
  //
  // Mantine caps content at 100dvh minus the y-offset on each side, but that
  // ignores our top margin, so subtract it too or the bottom still overflows.
  // Full screen needs the cap lifted outright: we don't pass Mantine's
  // `fullScreen` prop, so its 90dvh cap still applied and the page showed
  // through below a "full-screen" modal.
  const topMargin = navbarOffsetValue ?? (!isFullScreen ? marginTop : undefined);
  const contentMaxHeight = isFullScreen
    ? '100dvh'
    : topMargin
      ? `calc(100dvh - var(--modal-y-offset) * 2 - ${topMargin})`
      : undefined;

  const defaultStyles: ModalProps['styles'] = {
    content: {
      backgroundColor: '#1a1a1a',
      color: '#f2f2f2',
      overflowY: 'auto',
      ...(contentMaxHeight && { maxHeight: contentMaxHeight }),
      ...(isFullScreen && { height: '100dvh', borderRadius: 0 }),
      ...(marginTop && !isFullScreen && { marginTop }),
      ...(navbarOffsetValue && { marginTop: navbarOffsetValue }),
    },
    header: {
      backgroundColor: '#1a1a1a',
      color: '#f2f2f2',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
    title: {
      color: '#f2f2f2',
      fontWeight: 600,
      fontSize: isMobile ? '16px' : '18px',
    },
    body: {
      backgroundColor: '#1a1a1a',
      color: '#f2f2f2',
      paddingTop: '1rem',
      ...(isFullScreen && { paddingBottom: '2rem' }),
    },
    close: {
      color: '#f2f2f2',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    overlay: {
      zIndex: effectiveZIndex - 1,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
    root: {
      zIndex: effectiveZIndex,
    },
    inner: {
      ...(isFullScreen && { padding: 0, alignItems: 'flex-start' }),
    },
  };

  // Merge user-provided styles with defaults
  const mergedStyles: ModalProps['styles'] = {
    content: { ...defaultStyles.content, ...styles?.content },
    header: { ...defaultStyles.header, ...styles?.header },
    title: { ...defaultStyles.title, ...styles?.title },
    body: { ...defaultStyles.body, ...styles?.body },
    close: { ...defaultStyles.close, ...styles?.close },
    overlay: { ...defaultStyles.overlay, ...styles?.overlay },
    root: { ...defaultStyles.root, ...styles?.root },
    inner: { ...defaultStyles.inner, ...styles?.inner },
  };

  // When navbarOffset is used, we want to position from top, so disable centering
  // But respect explicit centered prop if provided
  const shouldCenter = props.centered !== undefined ? props.centered : navbarOffset ? false : true;

  return (
    <DropdownPortalContext.Provider value>
      <OverlayLayerContext.Provider value={effectiveZIndex}>
        <Modal
          title={title}
          zIndex={effectiveZIndex}
          size={modalSize}
          styles={mergedStyles}
          classNames={{
            ...classNames,
            content: ['themed-modal-content', classNames?.content].filter(Boolean).join(' '),
          }}
          centered={shouldCenter}
          {...props}
        />
      </OverlayLayerContext.Provider>
    </DropdownPortalContext.Provider>
  );
};

export default ThemedModal;
